import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectDomainApi } from '../services/projectDomainApi';
import type { CreateProjectDomainRequest, VerifyDiagnostic } from '../types/projectDomain';

// Thrown by useVerifyProjectDomain when the API returns a 422. Carries the
// per-record DNS diagnostics so the UI can render Current vs Expected
// inline rather than just a generic error string.
export class DomainVerifyError extends Error {
  diagnostics: VerifyDiagnostic[];
  constructor(message: string, diagnostics: VerifyDiagnostic[] = []) {
    super(message);
    this.name = 'DomainVerifyError';
    this.diagnostics = diagnostics;
  }
}

export const projectDomainsKeys = {
  all: ['projectDomains'] as const,
  list: (projectId: string) => [...projectDomainsKeys.all, 'list', projectId] as const,
  detail: (projectId: string, domainId: string) =>
    [...projectDomainsKeys.all, 'detail', projectId, domainId] as const,
};

export function useProjectDomains(projectId: string | undefined) {
  return useQuery({
    queryKey: projectDomainsKeys.list(projectId!),
    queryFn: async () => {
      const response = await projectDomainApi.list(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch domains');
      }
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useCreateProjectDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: CreateProjectDomainRequest;
    }) => {
      const response = await projectDomainApi.create(projectId, data);
      if (!response.success) {
        throw new Error(
          response.details?.join(', ') || response.error || 'Failed to create domain'
        );
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: projectDomainsKeys.list(variables.projectId) });
    },
  });
}

export function useDeleteProjectDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, domainId }: { projectId: string; domainId: string }) => {
      const response = await projectDomainApi.delete(projectId, domainId);
      if (!response.success) throw new Error('Failed to delete domain');
      return response;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: projectDomainsKeys.list(variables.projectId) });
    },
  });
}

export function useVerifyProjectDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, domainId }: { projectId: string; domainId: string }) => {
      const response = await projectDomainApi.verify(projectId, domainId);
      if (!response.success) {
        throw new DomainVerifyError(
          response.error_description || response.error || 'Verification failed',
          response.diagnostics ?? []
        );
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: projectDomainsKeys.list(variables.projectId) });
    },
  });
}

export function useSetPrimaryProjectDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, domainId }: { projectId: string; domainId: string }) => {
      const response = await projectDomainApi.setPrimary(projectId, domainId);
      if (!response.success) {
        throw new Error(response.error_description || response.error || 'Failed to set primary');
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: projectDomainsKeys.list(variables.projectId) });
    },
  });
}
